/**
 * Main JavaScript file (rewritten in vanilla JS) - Optimized for Performance
 * Fixed forced reflow issues for better PageSpeed performance
 */
import ScrollReveal from 'scrollreveal';
import ScrollMagic from 'scrollmagic';
import Masonry from 'masonry-layout';
import AnimatedHeadline from './animated-headline.js';
import BenefitsTabs from './benefits-tabs.js';

window.ScrollReveal = ScrollReveal;
window.ScrollMagic = ScrollMagic;
window.Masonry = Masonry;
function isMobile() {
    return window.innerWidth <= 767;
}

document.querySelectorAll('.footer-menu-holder').forEach(holder => {
    const menu = holder.nextElementSibling;

    holder.addEventListener('click', () => {
        if (!isMobile()) return;

        const isOpen = holder.classList.contains('active');

        if (isOpen) {
            menu.style.height = '0px';
            holder.classList.remove('active');
        } else {
            menu.style.height = menu.scrollHeight + 'px';
            holder.classList.add('active');
        }
    });
});

function resetFooter() {
    document.querySelectorAll('.footer-menu-holder').forEach(holder => {
        const menu = holder.nextElementSibling;
        holder.classList.remove('active');
        menu.style.height = '';
    });
}

window.addEventListener('resize', () => {
    if (!isMobile()) {
        resetFooter();
    }
});


// Utility functions to replace jQuery functionality
const Utils = {
    // Function to check DOM readiness
    ready(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback);
        } else {
            callback();
        }
    },

    // Optimized slide animations - Fixed forced reflow
    slideDown(element, duration = 300) {
        element.style.removeProperty('display');
        let display = window.getComputedStyle(element).display;
        if (display === 'none') display = 'block';
        
        element.style.display = display;
        
        // Cache geometric properties BEFORE making style changes
        const height = element.offsetHeight;
        const computedStyle = window.getComputedStyle(element);
        const paddingTop = parseFloat(computedStyle.paddingTop);
        const paddingBottom = parseFloat(computedStyle.paddingBottom);
        const marginTop = parseFloat(computedStyle.marginTop);
        const marginBottom = parseFloat(computedStyle.marginBottom);
        
        // Apply all style changes at once to minimize reflow
        requestAnimationFrame(() => {
            element.style.cssText += `
                overflow: hidden;
                height: 0;
                padding-top: 0;
                padding-bottom: 0;
                margin-top: 0;
                margin-bottom: 0;
                box-sizing: border-box;
                transition-property: height, margin, padding;
                transition-duration: ${duration}ms;
            `;
            
            // Force a reflow once, then apply final styles
            requestAnimationFrame(() => {
                element.style.height = height + 'px';
                element.style.paddingTop = paddingTop + 'px';
                element.style.paddingBottom = paddingBottom + 'px';
                element.style.marginTop = marginTop + 'px';
                element.style.marginBottom = marginBottom + 'px';
            });
        });
        
        setTimeout(() => {
            element.style.removeProperty('height');
            element.style.removeProperty('overflow');
            element.style.removeProperty('transition-duration');
            element.style.removeProperty('transition-property');
        }, duration);
    },

    slideUp(element, duration = 300) {
        // Cache geometric properties BEFORE making changes
        const height = element.offsetHeight;
        const computedStyle = window.getComputedStyle(element);
        
        requestAnimationFrame(() => {
            element.style.cssText += `
                transition-property: height, margin, padding;
                transition-duration: ${duration}ms;
                box-sizing: border-box;
                height: ${height}px;
                overflow: hidden;
            `;
            
            requestAnimationFrame(() => {
                element.style.height = '0';
                element.style.paddingTop = '0';
                element.style.paddingBottom = '0';
                element.style.marginTop = '0';
                element.style.marginBottom = '0';
            });
        });
        
        setTimeout(() => {
            element.style.display = 'none';
            element.style.removeProperty('height');
            element.style.removeProperty('padding-top');
            element.style.removeProperty('padding-bottom');
            element.style.removeProperty('margin-top');
            element.style.removeProperty('margin-bottom');
            element.style.removeProperty('overflow');
            element.style.removeProperty('transition-duration');
            element.style.removeProperty('transition-property');
        }, duration);
    },

    slideToggle(element, duration = 300) {
        if (window.getComputedStyle(element).display === 'none') {
            return this.slideDown(element, duration);
        } else {
            return this.slideUp(element, duration);
        }
    },

    fadeOut(element, duration = 200) {
        requestAnimationFrame(() => {
            element.style.cssText += `
                opacity: 1;
                transition: opacity ${duration}ms;
            `;
            
            requestAnimationFrame(() => {
                element.style.opacity = '0';
            });
        });
        
        setTimeout(() => {
            element.style.display = 'none';
            element.style.removeProperty('opacity');
            element.style.removeProperty('transition');
        }, duration);
    },

    // Optimized smooth scroll function - cached scroll position
    smoothScrollTo(target, offset = 0, duration = 300) {
        const targetElement = typeof target === 'string' ? document.querySelector(target) : target;
        if (!targetElement) return;

        // Cache all measurements at once
        const start = window.pageYOffset;
        const targetPosition = targetElement.getBoundingClientRect().top + start - offset;
        const distance = targetPosition - start;
        let startTime = null;

        const animation = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const run = this.easeInOutQuad(timeElapsed, start, distance, duration);
            window.scrollTo(0, run);
            if (timeElapsed < duration) requestAnimationFrame(animation);
        };

        requestAnimationFrame(animation);
    },

    // Easing function
    easeInOutQuad(t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
    },

    // ClickToggle functionality
    clickToggle(element, func1, func2) {
        let toggleState = 0;
        element.addEventListener('click', () => {
            if (toggleState === 0) {
                func1.call(element);
                toggleState = 1;
            } else {
                func2.call(element);
                toggleState = 0;
            }
        });
    }
};

// Main initialization function
const initializeApp = () => {
    Utils.ready(() => {
        // Scrolling functionality for buhpedija pages
        function scrollToTarget(target, offset) {
            const targetElement = document.querySelector(target);
            if (targetElement) {
                Utils.smoothScrollTo(targetElement, offset);
            }
        }

        // Scroll on bp-link click
        document.querySelectorAll('.bp-link').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const offset = 120;
                const target = link.getAttribute('href');

                // If this is a link to another page
                if (target.indexOf('#') === 0) {
                    // Local anchor on current page
                    scrollToTarget(target, offset);
                } else {
                    // Link to another page with anchor
                    window.location.href = target;
                }
            });
        });

        // Scroll to anchor on page load for buhpedija
        if (document.body.classList.contains('page-template-buhpedija')) {
            const hash = window.location.hash;
            if (hash && document.querySelector(hash)) {
                // Small delay for page loading
                setTimeout(() => {
                    scrollToTarget(hash, 120);
                }, 100);
            }

            // Handle anchor change in URL
            window.addEventListener('hashchange', () => {
                const newHash = window.location.hash;
                if (newHash && document.querySelector(newHash)) {
                    scrollToTarget(newHash, 120);
                }
            });
        }

        // AI Chat button
        const startAiChatBtn = document.getElementById('start-ai-chat');
        if (startAiChatBtn) {
            startAiChatBtn.addEventListener('click', (event) => {
                event.preventDefault();
                const chatButton = document.querySelector('.nbot-open-tray');
                if (chatButton) {
                    chatButton.click();
                }
            });
        }

        // Expand benefits
        document.querySelectorAll('.expand-benefits').forEach(btn => {
            btn.addEventListener('click', () => {
                const benefits = btn.closest('.benefits');
                const additionalBenefits = benefits.querySelector('.additional-benefits');
                if (additionalBenefits) {
                    Utils.slideDown(additionalBenefits);
                }
                Utils.fadeOut(btn, 200);
            });
        });

        // Mobile/Desktop link detection
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            // Mobile device
            document.querySelectorAll('.mobile-link').forEach(link => {
                link.classList.remove('hide-link');
                link.classList.add('show-link');
            });
            document.querySelectorAll('.desktop-link').forEach(link => {
                link.classList.remove('show-link');
                link.classList.add('hide-link');
            });
        } else {
            // Desktop
            document.querySelectorAll('.desktop-link').forEach(link => {
                link.classList.remove('hide-link');
                link.classList.add('show-link');
            });
            document.querySelectorAll('.mobile-link').forEach(link => {
                link.classList.remove('show-link');
                link.classList.add('hide-link');
            });
        }

        // Mobile navigation toggle - optimized scroll handling
        document.querySelectorAll('.mobile-nav ul li.menu-item-has-children > a').forEach(link => {
            Utils.clickToggle(link, 
                function() {
                    // Reset other expanded items
                    document.querySelectorAll('.mobile-nav ul li.menu-item-has-children > .sub-menu').forEach(menu => {
                        if (menu !== this.nextElementSibling) {
                            menu.classList.remove('expanded');
                        }
                    });
                    document.querySelectorAll('.mobile-nav ul li.menu-item-has-children > a').forEach(a => {
                        if (a !== this) {
                            a.classList.remove('expanded');
                        }
                    });
                    
                    this.nextElementSibling.classList.add('expanded');
                    this.classList.add('expanded');
                    
                    const mobileNavContainer = document.querySelector('.mobile-nav-container');
                    if (mobileNavContainer) {
                        // Use requestAnimationFrame for smooth scroll
                        requestAnimationFrame(() => {
                            mobileNavContainer.scrollTop = 0;
                        });
                    }
                },
                function() {
                    this.nextElementSibling.classList.remove('expanded');
                    this.classList.remove('expanded');
                    
                    const mobileNavContainer = document.querySelector('.mobile-nav-container');
                    if (mobileNavContainer) {
                        requestAnimationFrame(() => {
                            mobileNavContainer.scrollTop = 0;
                        });
                    }
                }
            );
        });

        // Window resize handler
        window.addEventListener('resize', () => {
            if (window.innerWidth > 991) {
                document.body.classList.remove('mobile-menu-open');
            }
        });

        // Masonry initialization - optimized
        const gridElements = document.querySelectorAll('.grid');
        if (gridElements.length > 0) {
            // Use requestAnimationFrame to avoid blocking main thread
            requestAnimationFrame(() => {
                gridElements.forEach(gridElement => {
                    const masonry = new Masonry(gridElement, {
                        itemSelector: '.grid-item',
                        columnWidth: '.grid-sizer',
                        percentPosition: true,
                        horizontalOrder: false
                    });

                    gridElement._masonry = masonry;
                });
            });
        }

        // SCROLL handling - throttled for better performance
        let lastScrollTop = 0;
        let scrollTimeout = null;
        
        const handleScroll = () => {
            const st = window.pageYOffset;

            if (st > lastScrollTop && st > 300) {
                document.body.classList.add('scrollingDown');
            } else {
                document.body.classList.remove('scrollingDown');
            }

            if (st > 100) {
                document.body.classList.add('scrolledDown');
            } else {
                document.body.classList.remove('scrolledDown');
            }

            lastScrollTop = st;
        };

        window.addEventListener('scroll', () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            
            // Throttle scroll events for better performance
            scrollTimeout = setTimeout(handleScroll, 10);
        }, { passive: true });

        // Variations handling
        document.querySelectorAll('.variations').forEach(variation => {
            const firstInput = variation.querySelector('input:first-of-type');
            if (firstInput) {
                firstInput.checked = true;
            }
        });

        document.querySelectorAll('.wc-pao-addon-container').forEach(container => {
            const firstInput = container.querySelector('input:first-of-type');
            if (firstInput) {
                firstInput.checked = true;
            }
        });

        document.querySelectorAll('.wc-pao-addon-wrap').forEach(wrap => {
            const firstSwatch = wrap.querySelector('.wc-pao-addon-image-swatch:first-child');
            if (firstSwatch) {
                firstSwatch.classList.add('selected');
            }
        });

        // Mobile nav button
        const mobileNavBtn = document.querySelector('.mobile-nav-btn');
        if (mobileNavBtn) {
            mobileNavBtn.addEventListener('click', () => {
                document.body.classList.toggle('mobile-menu-open');
            });
        }

        // Sticky header title
        const stickyHeaderTitle = document.querySelector('.sticky-header-title');
        if (stickyHeaderTitle) {
            stickyHeaderTitle.addEventListener('click', () => {
                Utils.smoothScrollTo(document.body, 0, 600);
            });
        }

        // Service item title formatting
        document.querySelectorAll('.service-item-inner .service-item-title').forEach(title => {
            if (title.textContent.includes('/')) {
                title.innerHTML = title.innerHTML.replace(/\//g, '<span class="lighter-text">/</span>');
            }
        });

        // Main navigation
        document.querySelectorAll('.main-navigation .menu-item-has-children > a').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
            });
        });

        document.querySelectorAll('.main-navigation .menu-item-has-children').forEach(menuItem => {
            const link = menuItem.querySelector('a:first-child');
            
            ['mouseenter', 'focusin', 'click'].forEach(event => {
                menuItem.addEventListener(event, () => {
                    menuItem.classList.add('active');
                    if (link) link.setAttribute('aria-expanded', 'true');
                });
            });

            ['mouseleave', 'focusout'].forEach(event => {
                menuItem.addEventListener(event, () => {
                    menuItem.classList.remove('active');
                    if (link) link.setAttribute('aria-expanded', 'false');
                });
            });
        });

        document.querySelectorAll('.sub-menu').forEach(subMenu => {
            ['mouseleave', 'focusout'].forEach(event => {
                subMenu.addEventListener(event, () => {
                    subMenu.classList.remove('active');
                    const parentLink = subMenu.parentElement.querySelector('a:first-child');
                    if (parentLink) parentLink.setAttribute('aria-expanded', 'false');
                });
            });
        });

        // Modal functions
        function getOfferModalOpen() {
            const serviceTitle = document.querySelector('.single-service h1.entry-title');
            const interestInput = document.getElementById('input_1_6');
            
            if (serviceTitle && interestInput) {
                interestInput.value = serviceTitle.textContent;
            }
            
            setTimeout(() => {
                const firstInput = document.getElementById('input_1_1');
                if (firstInput) firstInput.focus();
            }, 2000);
            
            const modal = document.querySelector('.modal-window.get-offer-modal');
            const form = document.querySelector('.get-offer-modal-form');
            
            if (modal) modal.classList.add('active');
            document.body.classList.add('modal-window-open');
            
            setTimeout(() => {
                if (form) form.classList.add('float-up');
            }, 350);
        }

        function downloadSetModalOpen() {
            const modal = document.querySelector('.modal-window.download-document-set-modal');
            const form = document.querySelector('.get-offer-modal-form');
            
            if (modal) modal.classList.add('active');
            document.body.classList.add('modal-window-open');
            
            setTimeout(() => {
                if (form) form.classList.add('float-up');
            }, 350);
        }

        function closeModals() {
            document.querySelectorAll('.modal-window').forEach(modal => {
                modal.classList.remove('active');
            });

            setTimeout(() => {
                const form = document.querySelector('.get-offer-modal-form');
                if (form) form.classList.remove('float-up');
            });

            document.body.classList.remove('modal-window-open');
            document.body.classList.remove('mobile-menu-open');
        }

        // Keyboard events
        document.addEventListener('keyup', (evt) => {
            if (evt.keyCode === 27) {
                closeModals();
            }
        });

        // Modal close buttons
        document.querySelectorAll('.modal-close-btn').forEach(btn => {
            btn.addEventListener('click', closeModals);
        });

        function addServiceListeners() {
            setTimeout(closeModals, 4500);
        }

        // Gravity Forms integration
        document.addEventListener('gform_confirmation_loaded', () => {
            const formTitle = document.querySelector('.get-offer-form-container h2');
            const profile = document.querySelector('.get-offer-profile');
            
            if (formTitle) formTitle.remove();
            if (profile) profile.remove();
            addServiceListeners();
        });

        // Get offer button
        document.querySelectorAll('.get-offer-btn').forEach(btn => {
            btn.addEventListener('click', getOfferModalOpen);
        });

        // Document set download
        document.querySelectorAll('.doc-set-download').forEach(btn => {
            btn.addEventListener('click', () => {
                downloadSetModalOpen();
                
                const docsetId = btn.getAttribute('doc-set');
                const docsetInput = document.getElementById('input_12_4');
                if (docsetInput && docsetId) {
                    docsetInput.value = docsetId;
                }
            });
        });

        // Footer offer focus
        document.querySelectorAll('.footer-offer .ginput_container input').forEach(input => {
            input.addEventListener('focus', () => {
                const extraField = document.querySelector('.footer-offer .extra-field');
                if (extraField) {
                    Utils.slideDown(extraField);
                }
            });
        });

        // Remove FAQ items
        document.querySelectorAll('.cfaq_noQuestion').forEach(item => {
            item.remove();
        });

        // Initialize Animated Headlines (only on desktop)
        if (!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            // Debug: check for elements
            console.log('🎯 Looking for .cd-headline elements:', document.querySelectorAll('.cd-headline').length);
            
            // Initialize animated headlines
            AnimatedHeadline.initAll('.cd-headline', {
                animationType: 'rotate-1',
                animationDelay: 2500,
                lettersDelay: 50
            });
        }

        // Gravity Forms post render
        document.addEventListener('gform_post_render', () => {
            document.querySelectorAll('.footer-offer .ginput_container input').forEach(input => {
                input.addEventListener('focus', () => {
                    const extraField = document.querySelector('.gform_validation_error .extra-field');
                    if (extraField) {
                        Utils.slideDown(extraField);
                    }
                });
            });
            
            const extraField = document.querySelector('.gform_validation_error .extra-field');
            if (extraField) {
                Utils.slideDown(extraField);
            }
        });

        // Analytics tracking
        document.addEventListener('gform_confirmation_loaded', (event) => {
            const formId = event.detail?.formId;
            
            if (formId === 1 || formId === 3) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'send', {
                        'event_category': 'mygtukas',
                        'event_label': 'paraiska_LT',
                        'event_action': 'gauti_pasiulyma'
                    });
                }
            } else if (formId === 4 || formId === 5) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'send', {
                        'event_category': 'mygtukas',
                        'event_label': 'paraiska_EN',
                        'event_action': 'gauti_pasiulyma'
                    });
                }
            } else if (formId === 6) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'send', {
                        'event_category': 'mygtukas',
                        'event_label': 'paraiska_RU',
                        'event_action': 'gauti_pasiulyma'
                    });
                }
            } else if (formId === 12) {
                const downloadLink = document.getElementById('generated-download-link');
                if (downloadLink) {
                    const downloadUrl = downloadLink.getAttribute('href');
                    if (downloadUrl) {
                        setTimeout(() => {
                            window.location = downloadUrl;
                        }, 2000);
                    }
                }
            }
        });

        // Download button tracking
        document.querySelectorAll('.download-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const name = btn.getAttribute('doctitle');
                if (typeof gtag !== 'undefined' && name) {
                    gtag('event', 'send', {
                        'event_category': 'document',
                        'event_action': 'download',
                        'event_label': name,
                    });
                }
            });
        });

        // Annual jobs pricing
        const expandedJob = document.querySelector('.annual-jobs > li.expanded > .pricing-line-header');
        if (expandedJob) {
            const instructions = expandedJob.nextElementSibling;
            if (instructions && instructions.classList.contains('instructions')) {
                Utils.slideToggle(instructions);
                expandedJob.classList.toggle('active');
            }
        }

        document.querySelectorAll('.annual-jobs > li.has-instructions > .pricing-line-header').forEach(header => {
            header.addEventListener('click', function() {
                // Close other active items
                document.querySelectorAll('.annual-jobs > li.has-instructions > .pricing-line-header').forEach(otherHeader => {
                    if (otherHeader !== this) {
                        otherHeader.classList.remove('active');
                        const otherInstructions = otherHeader.nextElementSibling;
                        if (otherInstructions && otherInstructions.classList.contains('instructions')) {
                            Utils.slideUp(otherInstructions);
                        }
                    }
                });

                this.classList.toggle('active');
                const instructions = this.nextElementSibling;
                if (instructions && instructions.classList.contains('instructions')) {
                    Utils.slideToggle(instructions);
                }
            });
        });

        // Initialize Benefits Tabs
        BenefitsTabs.init();
    });
};

// Search modal functionality
Utils.ready(() => {
    // Open search modal
    document.querySelectorAll('.menu-search-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const searchModal = document.querySelector('.search-modal');
            if (searchModal) {
                searchModal.classList.add('active');
                setTimeout(() => {
                    const searchField = document.getElementById('search-field');
                    if (searchField) {
                        searchField.focus();
                    }
                }, 300);
            }
        });
    });

    // Close search modal
    document.querySelectorAll('.search-close-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const searchModal = document.querySelector('.search-modal');
            if (searchModal) {
                searchModal.classList.remove('active');
            }
        });
    });

    // Close modal with ESC key
    document.addEventListener('keyup', (event) => {
        if (event.key === "Escape") {
            const searchModal = document.querySelector('.search-modal');
            if (searchModal) {
                searchModal.classList.remove('active');
            }
        }
    });
});

// ScrollReveal animations - optimized
Utils.ready(() => {
    document.body.classList.add('js-sr');

    const initScrollReveal = () => {
        if (typeof ScrollReveal === 'undefined') {
            return false;
        }

        try {
            const sr = ScrollReveal({
                duration: 1000,
                distance: '50px',
                easing: 'ease-out',
                origin: 'bottom',
                reset: false,
                mobile: true,
                scale: 1
            });

            // Use requestAnimationFrame to avoid blocking main thread during initialization
            requestAnimationFrame(() => {
                if (window.innerWidth < 960) {
                    // Mobile settings
                    sr.reveal('.slide-up', {
                        origin: 'bottom',
                        duration: 800,
                        distance: '30px',
                        delay: 100
                    });

                    sr.reveal('.slide-up-slow', {
                        origin: 'bottom',
                        duration: 1200,
                        distance: '40px',
                        delay: 200
                    });

                    sr.reveal('.slide-right', {
                        origin: 'bottom',
                        duration: 800,
                        distance: '30px',
                        delay: 150
                    });

                    sr.reveal('.slide-left', {
                        origin: 'bottom',
                        duration: 800,
                        distance: '30px',
                        delay: 150
                    });

                    // sr.reveal('.slide-left-home', {
                    //     origin: 'right',
                    //     duration: 1000,
                    //     distance: '50px',
                    //     delay: 100
                    // });

                    sr.reveal('.slide-left-delay', {
                        origin: 'right',
                        duration: 1000,
                        distance: '50px',
                        delay: 300
                    });

                } else {
                    // Desktop settings
                    sr.reveal('.slide-up', {
                        origin: 'bottom',
                        duration: 1000,
                        distance: '60px',
                        delay: 100
                    });

                    sr.reveal('.slide-up-slow', {
                        origin: 'bottom',
                        duration: 1600,
                        distance: '80px',
                        delay: 200
                    });

                    sr.reveal('.slide-right', {
                        origin: 'left',
                        duration: 1200,
                        distance: '100px',
                        delay: 150
                    });

                    sr.reveal('.slide-left', {
                        origin: 'right',
                        duration: 1200,
                        distance: '100px',
                        delay: 150
                    });

                    sr.reveal('.slide-left-home', {
                        origin: 'right',
                        duration: 1400,
                        distance: '120px',
                        delay: 100
                    });

                    sr.reveal('.slide-left-delay', {
                        origin: 'right',
                        duration: 1400,
                        distance: '120px',
                        delay: 400
                    });
                }

                console.log('✅ ScrollReveal animations initialized successfully!');
            });

            return true;

        } catch (error) {
            console.error('❌ Error initializing ScrollReveal:', error);
            return false;
        }
    };

    const success = initScrollReveal();

    if (!success) {
        console.warn('⚠️ ScrollReveal failed to initialize. Retrying with delay...');
        
        setTimeout(() => {
            console.log('🔄 Retrying ScrollReveal initialization...');
            const retrySuccess = initScrollReveal();

            if (!retrySuccess) {
                console.error('❌ Retry failed.');
            }
        }, 500);
    }


    window.retryScrollReveal = function() {
        console.log('🔄 Retrying...');
        document.body.classList.add('js-sr');
        initScrollReveal();
    };
});

// Calculator functions - unchanged as they don't cause reflow issues
function roundToTwoDigitsAfterComma(floatNumber) {
    return parseFloat((Math.round(floatNumber * 100) / 100).toFixed(2));
}

function first_input(value) {
    const gpm = document.querySelector('#gpm');
    const second_input = document.querySelector('#remainsaftertaxes');
    
    if (gpm) gpm.value = roundToTwoDigitsAfterComma(value * 0.15);
    if (second_input) second_input.value = roundToTwoDigitsAfterComma(value - (gpm ? gpm.value : 0));
}

function second_input(value) {
    const gpm = document.querySelector('#gpm');
    const first_input = document.querySelector('#pricefromprofit');
    
    if (first_input) first_input.value = roundToTwoDigitsAfterComma(value / 0.85);
    if (gpm && first_input) gpm.value = roundToTwoDigitsAfterComma(first_input.value * 0.15);
}

function f1_2_first_input(value) {
    const gpm = document.querySelector('#gpm');
    const second_input = document.querySelector('#remainsaftertaxes');
    const fullprice = document.querySelector('#fullplaceprice');
    const first_input = document.querySelector('#pricefromprofit');
    
    if (gpm) gpm.value = roundToTwoDigitsAfterComma(value * 0.15);
    if (second_input) second_input.value = roundToTwoDigitsAfterComma(value);
    
    if (first_input && gpm && fullprice) {
        const fullpricevalue = parseFloat(first_input.value) + parseFloat(gpm.value);
        fullprice.value = fullpricevalue;
    }
}

function f1_2_second_input(value) {
    const gpm = document.querySelector('#gpm');
    const first_input = document.querySelector('#pricefromprofit');
    const fullprice = document.querySelector('#fullplaceprice');
    
    if (first_input) first_input.value = roundToTwoDigitsAfterComma(value);
    if (gpm && first_input) gpm.value = roundToTwoDigitsAfterComma(first_input.value * 0.15);
    
    if (first_input && gpm && fullprice) {
        const fullpricevalue = parseFloat(first_input.value) + parseFloat(gpm.value);
        fullprice.value = fullpricevalue;
    }
}

function f1_2_third_input(value) {
    const gpm = document.querySelector('#gpm');
    const first_input = document.querySelector('#pricefromprofit');
    const fullprice = document.querySelector('#fullplaceprice');
    const second_input = document.querySelector('#remainsaftertaxes');
    
    if (first_input) first_input.value = roundToTwoDigitsAfterComma(value / 1.15);
    if (second_input) second_input.value = roundToTwoDigitsAfterComma(value / 1.15);
    if (gpm && first_input) gpm.value = roundToTwoDigitsAfterComma(value - first_input.value);
}

function f2_first_input(value) {
    const gpm = document.querySelector('#f2_gpm');
    const first_input = document.querySelector('#f2_pricefromprofit');
    const second_input = document.querySelector('#f2_remainsaftertaxes');
    
    if (gpm && first_input) gpm.value = roundToTwoDigitsAfterComma(first_input.value * 0.15);
    if (second_input && first_input) second_input.value = first_input.value;
}

function f2_second_input(value) {
    const gpm = document.querySelector('#f2_gpm');
    const first_input = document.querySelector('#f2_pricefromprofit');
    const second_input = document.querySelector('#f2_remainsaftertaxes');
    
    if (gpm && second_input) gpm.value = roundToTwoDigitsAfterComma(second_input.value * 0.15);
    if (first_input && second_input) first_input.value = second_input.value;
}


// Global functions for calculator
window.first_input = first_input;
window.second_input = second_input;
window.f1_2_first_input = f1_2_first_input;
window.f1_2_second_input = f1_2_second_input;
window.f1_2_third_input = f1_2_third_input;
window.f2_first_input = f2_first_input;
window.f2_second_input = f2_second_input;

// Initialize the application
initializeApp();

const headerSearch = document.querySelector('.header-search');
const searchDropdown = document.querySelector('.search-dropdown');
const bodyEl = document.body;

if (headerSearch && searchDropdown) {

    headerSearch.addEventListener('click', function (e) {
        e.stopPropagation(); // IMPORTANT

        searchDropdown.classList.toggle('active');
        bodyEl.classList.toggle('overlay');
    });

    searchDropdown.addEventListener('click', function (e) {
        e.stopPropagation(); // IMPORTANT
    });

    document.addEventListener('click', function () {
        searchDropdown.classList.remove('active');
        bodyEl.classList.remove('overlay');
    });

    document.addEventListener('keydown', function (e) {
        if (e.key === "Escape") {
            searchDropdown.classList.remove('active');
            bodyEl.classList.remove('overlay');
        }
    });
}

document.querySelectorAll('.tab-panel').forEach(panel => {

    const cards = panel.querySelectorAll('.card-item');
    const button = panel.querySelector('.show-more-btn');
    console.log(cards)
    if (!cards.length || !button) return;

    if (cards.length <= 4) {
        button.style.display = 'none';
        return;
    }

    button.addEventListener('click', () => {
        console.log(panel)
        panel.querySelectorAll('.card-item.hidden')
            .forEach(card => card.classList.remove('hidden'));

        button.style.display = 'none';
    });

});
